import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createCollectorEnquiry } from '../supabase';
import './BecomeCollectorPage.css';

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5.5 19c1.5-3 3.8-4.5 6.5-4.5s5 1.5 6.5 4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 8l7 5 7-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4.5h2.2l1.2 3-1.6 1.2a10 10 0 0 0 5.5 5.5l1.2-1.6 3 1.2V18a1.5 1.5 0 0 1-1.5 1.5A12.5 12.5 0 0 1 6.5 7 1.5 1.5 0 0 1 8 4.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BusinessIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 19V8.5L12 4l8 4.5V19H4z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9 19v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 11.5L20 4l-5.5 16-3.2-6.3L4 11.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

const INITIAL_FORM = {
  full_name: '',
  email: '',
  phone: '',
  business_name: '',
  message: '',
};

export default function BecomeCollectorPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await createCollectorEnquiry({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        business_name: form.business_name.trim() || '',
        message: form.message.trim() || '',
      });
      setSubmitted(true);
      setForm(INITIAL_FORM);
    } catch (err) {
      setError(err?.message || 'Could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="collector-page">
      <section className="collector-hero" aria-labelledby="collector-heading">
        <div className="collector-hero-inner">
          <h1 id="collector-heading">Become a Collector</h1>
          <p>
            Join SnapScrap as a licensed scrap collector. Get matched with nearby scrap posts and
            grow your collections.
          </p>
        </div>
      </section>

      <section className="collector-body" aria-label="Become a collector form">
        <div className="collector-inner">
          {submitted ? (
            <div className="collector-success">
              <h2>Application received</h2>
              <p>
                Thanks for applying. Our team will review your collector details and contact you
                about next steps and verification.
              </p>
              <div className="collector-success-actions">
                <button
                  type="button"
                  className="collector-submit"
                  onClick={() => setSubmitted(false)}
                >
                  Submit another application
                </button>
                <Link className="collector-secondary-link" to="/post-scrap">
                  Browse Post Scrap
                </Link>
              </div>
            </div>
          ) : (
            <form className="collector-form" onSubmit={handleSubmit}>
              <label className="collector-field">
                <span>
                  Full Name <em>*</em>
                </span>
                <span className="collector-input">
                  <UserIcon />
                  <input
                    name="fullName"
                    type="text"
                    placeholder="Your full name"
                    required
                    value={form.full_name}
                    onChange={(e) => updateField('full_name', e.target.value)}
                  />
                </span>
              </label>

              <label className="collector-field">
                <span>
                  Email Address <em>*</em>
                </span>
                <span className="collector-input">
                  <MailIcon />
                  <input
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                  />
                </span>
              </label>

              <label className="collector-field">
                <span>
                  Phone Number <em>*</em>
                </span>
                <span className="collector-input">
                  <PhoneIcon />
                  <input
                    name="phone"
                    type="tel"
                    placeholder="Your phone number"
                    required
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                  />
                </span>
              </label>

              <label className="collector-field">
                <span>Business Name</span>
                <span className="collector-input">
                  <BusinessIcon />
                  <input
                    name="businessName"
                    type="text"
                    placeholder="Your business name (optional)"
                    value={form.business_name}
                    onChange={(e) => updateField('business_name', e.target.value)}
                  />
                </span>
              </label>

              <label className="collector-field">
                <span>Message</span>
                <textarea
                  name="message"
                  placeholder="Tell us anything else about your collecting work (optional)"
                  maxLength={1000}
                  rows={5}
                  value={form.message}
                  onChange={(e) => updateField('message', e.target.value)}
                />
                <small>{form.message.length} / 1000 characters</small>
              </label>

              {error ? (
                <p className="collector-error" role="alert">
                  {error}
                </p>
              ) : null}

              <button type="submit" className="collector-submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Become a Collector'}
                {!submitting ? <SendIcon /> : null}
              </button>
            </form>
          )}

          <aside className="collector-aside">
            <div className="collector-tip">
              <h2>Why collect with SnapScrap?</h2>
              <ul>
                <li>Get matched with local scrap posts</li>
                <li>Choose jobs that fit your vehicle</li>
                <li>Build trust with verified listings</li>
                <li>Grow repeat commercial collections</li>
              </ul>
            </div>
            <div className="collector-tip">
              <h2>Have scrap to move?</h2>
              <p>Post your scrap for free and get collectors to pick it up.</p>
              <Link className="collector-aside-btn" to="/post-scrap">
                Post Scrap →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
